// ============================================================
// AdjustmentPanel.jsx — the 14 fine-tune sliders
// ------------------------------------------------------------
// Driven entirely by ADJUSTMENTS metadata from filterEngine.js. Each row:
// icon + label, a teal range slider, a live value readout, and double-click
// to reset that one slider. "Reset All" sits at the bottom.
// ============================================================

import React from 'react';
import { ADJUSTMENTS, formatValue } from '../../utils/filterEngine';

// Compact 16×16 stroke icons, same visual language as the sidebar NavIcon.
function AdjIcon({ id }) {
  const paths = {
    'sun':        <><circle cx="8" cy="8" r="3.2" /><path d="M8 1v2M8 13v2M1 8h2M13 8h2M3.3 3.3l1.4 1.4M11.3 11.3l1.4 1.4M3.3 12.7l1.4-1.4M11.3 4.7l1.4-1.4" /></>,
    'contrast':   <><circle cx="8" cy="8" r="6.5" /><path d="M8 1.5a6.5 6.5 0 010 13z" fill="currentColor" stroke="none" /></>,
    'drop':       <path d="M8 1.5C8 1.5 3 7 3 10.2A5 5 0 008 15a5 5 0 005-4.8C13 7 8 1.5 8 1.5z" />,
    'circle-half':<><circle cx="8" cy="8" r="6.5" /><path d="M8 1.5v13" /></>,
    'thermo':     <><path d="M9 9.3V3a1.5 1.5 0 00-3 0v6.3a3 3 0 103 0z" /></>,
    'sun-high':   <><circle cx="8" cy="9" r="2.6" /><path d="M8 3v1.5M3.8 5.5l1 1M12.2 5.5l-1 1" /></>,
    'moon':       <path d="M13 9.5A5.5 5.5 0 116.5 3a4.5 4.5 0 006.5 6.5z" />,
    'fade':       <><rect x="2" y="2.5" width="12" height="11" rx="2" /><path d="M2 8h12" opacity="0.5" /></>,
    'vignette':   <><rect x="1.5" y="1.5" width="13" height="13" rx="3" /><circle cx="8" cy="8" r="3.5" /></>,
    'triangle':   <path d="M8 2l6 11H2z" />,
    'grid':       <><rect x="2" y="2" width="12" height="12" rx="1.5" /><path d="M2 6h12M2 10h12M6 2v12M10 2v12" opacity="0.7" /></>,
    'aperture':   <><circle cx="8" cy="8" r="6.5" /><path d="M8 1.5L11 8M14.5 8L8 8M8 14.5L5 8M1.5 8L8 8" opacity="0.7" /></>,
    'noise':      <><circle cx="4" cy="4" r="0.8" /><circle cx="9" cy="3.5" r="0.8" /><circle cx="12" cy="6" r="0.8" /><circle cx="5.5" cy="8" r="0.8" /><circle cx="11" cy="10" r="0.8" /><circle cx="4" cy="12" r="0.8" /><circle cx="8" cy="12.5" r="0.8" /><circle cx="13" cy="13" r="0.8" /></>,
    'palette':    <><path d="M8 1.5a6.5 6.5 0 000 13c1 0 1.5-.8 1.5-1.5 0-1 .8-1.5 1.5-1.5h1A2.5 2.5 0 0014.5 9 6.5 6.5 0 008 1.5z" /><circle cx="5.5" cy="6" r="0.9" /><circle cx="9" cy="4.8" r="0.9" /><circle cx="11.3" cy="7" r="0.9" /></>,
  };
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor"
      strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      {paths[id] || <circle cx="8" cy="8" r="5" />}
    </svg>
  );
}

export default function AdjustmentPanel({ adjustments, onChange, onReset, onResetAll, onSaveCustom }) {
  return (
    <div className="pe-adjust">
      {ADJUSTMENTS.map((adj) => {
        const value = adjustments[adj.key];
        const atDefault = value === adj.def;
        return (
          <div className="pe-adj-row" key={adj.key}>
            <div className="pe-adj-head">
              <span className="pe-adj-label">
                <AdjIcon id={adj.icon} /> {adj.label}
              </span>
              <span
                className={`pe-adj-val${atDefault ? '' : ' changed'}`}
                onDoubleClick={() => onReset(adj.key)}
                title="Double-click to reset"
              >
                {formatValue(adj, value)}
              </span>
            </div>
            <input
              type="range"
              className="pe-range"
              min={adj.min}
              max={adj.max}
              value={value}
              onChange={(e) => onChange(adj.key, Number(e.target.value))}
              onDoubleClick={() => onReset(adj.key)}
            />
          </div>
        );
      })}
      {onSaveCustom && (
        <button className="pe-btn pe-btn-accent pe-btn-block" onClick={onSaveCustom}>
          ＋ Save as Custom Filter
        </button>
      )}
      <button className="pe-btn pe-btn-ghost pe-reset-all" onClick={onResetAll}>
        Reset All
      </button>
    </div>
  );
}
